import React, { useState, useEffect, useRef } from "react";
import { Form, Input, message } from "antd";
import styled from "styled-components";
import { userService } from "@/api/user";
import { AppSelect } from "@/shared/components";
import { DynamicModal } from "@/shared/components/Modal/DynamicModal";
import type { ClientInvitation, Participant } from "@/shared/types/campaign/types";
import { ParticipantTable } from "./ParticipantTable";
import { normalizeEmail } from "@/shared/utils/email";

type Section = "clients" | "agency";

// The email check's message comes and goes as the user types. DynamicModal
// zeroes the last field's margin, so without a reserved line the modal jumps
// in height each time: keep room for one help line (44px input + 22px).
const ReservedHelpForm = styled(Form)`
	.cv-reserve-help .ant-form-item-control {
		min-height: 66px;
	}
` as typeof Form;

interface Props {
	participants: Participant[];
	// Open client invitations (emails with no account yet). Shown in the
	// Clients table with their status; unsaved ones carry a temp id.
	clientInvitations?: ClientInvitation[];
	onUpdate: (participants: Participant[]) => void;
	// Add + Remove are both buffered — parent owns state patches and enqueues.
	// We still do a client-side email existence check before queuing so the
	// user gets immediate feedback for typos / unregistered emails. Clients
	// may be unregistered (`existing` false): they get an invitation instead.
	onAddParticipant: (email: string, role: string, displayName?: string, existing?: boolean) => void;
	onRemoveParticipant: (userId: string) => void;
	onRevokeInvitation?: (invitationId: string) => void;
	onResendInvitation?: (invitationId: string) => void;
	canManage?: boolean;
}

export const CollaboratorsTab: React.FC<Props> = ({
	participants,
	clientInvitations = [],
	onAddParticipant,
	onRemoveParticipant,
	onRevokeInvitation,
	onResendInvitation,
	canManage = true,
}) => {
	const [modalOpen, setModalOpen] = useState(false);
	const [section, setSection] = useState<Section>("agency");
	const [form] = Form.useForm();
	const [formValid, setFormValid] = useState(false);
	const [isCheckingEmail, setIsCheckingEmail] = useState(false);
	const [emailExists, setEmailExists] = useState<boolean | null>(null);
	// Inline error when the typed email (case-insensitive) is already a
	// participant or has an open client invitation, or null when there's no
	// conflict. Drives both the inline form error and the Submit-time guard.
	// We re-derive this from the current participants and invitations
	// whenever the email changes so it stays accurate as buffered
	// adds/removes happen elsewhere.
	const [duplicateHelp, setDuplicateHelp] = useState<string | null>(null);
	const [adding, setAdding] = useState(false);
	const debounceRef = useRef<ReturnType<typeof setTimeout>>();

	// Clients can be invited before they have an account; agency members can't.
	const emailAccepted = emailExists === true || (section === "clients" && emailExists === false);
	const isFormValid = (v: { email?: string; role?: string }) =>
		!!v.email && !isCheckingEmail && emailAccepted && !duplicateHelp && (section !== "agency" || !!v.role);

	useEffect(() => {
		setFormValid(isFormValid(form.getFieldsValue()));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [emailExists, isCheckingEmail, duplicateHelp, section, form]);

	const agency = participants.filter((p) => !["Client", "Brand", "Creator"].includes(p.role));
	const clients: Participant[] = [
		...participants
			.filter((p) => p.role === "Client" || p.role === "Brand")
			.map((p) => ({ ...p, status: p.status ?? ("active" as const) })),
		...clientInvitations.map((i, index) => ({
			key: participants.length + index + 1,
			userId: i.invitationId,
			name: "",
			email: i.email,
			role: "Client",
			status: i.status,
		})),
	];
	const creators = participants.filter((p) => p.role === "Creator");
	const invitationIds = new Set(clientInvitations.map((i) => i.invitationId));

	const openModal = (s: Section) => {
		setSection(s);
		setModalOpen(true);
	};
	const closeModal = () => {
		setModalOpen(false);
		form.resetFields();
		setFormValid(false);
		setEmailExists(null);
		setDuplicateHelp(null);
	};

	// Look up an existing participant or open invitation by email
	// (case-insensitive). Pulled out of the change handler so the
	// submit-time guard and the inline-typing guard share one definition
	// of "already in this campaign".
	const findDuplicate = (email: string): string | null => {
		const normalized = normalizeEmail(email);
		if (!normalized) return null;
		const participant = participants.find((p) => normalizeEmail(p.email) === normalized);
		if (participant) return `Already in this campaign as ${participant.role || "a participant"}.`;
		if (clientInvitations.some((i) => normalizeEmail(i.email) === normalized)) {
			return "Already invited to this campaign.";
		}
		return null;
	};

	const handleRemove = (userId: string) => {
		// Buffered: parent enqueues the op and patches local participants.
		onRemoveParticipant(userId);
	};

	const handleRemoveClient = (id: string) => {
		if (invitationIds.has(id)) {
			onRevokeInvitation?.(id);
			return;
		}
		handleRemove(id);
	};

	const handleEmailChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const email = e.target.value;
		const normalizedEmail = normalizeEmail(email);
		// Eager duplicate check — runs on every keystroke, no debounce needed
		// since it's a local lookup against the participants array.
		setDuplicateHelp(findDuplicate(email));

		if (!normalizedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
			setEmailExists(null);
			setIsCheckingEmail(false);
			return;
		}
		setIsCheckingEmail(true);
		clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(async () => {
			try {
				setEmailExists(Boolean((await userService.checkExists(normalizedEmail))?.email));
			} catch {
				setEmailExists(null);
			} finally {
				setIsCheckingEmail(false);
			}
		}, 500);
	};

	const handleOk = async () => {
		try {
			const values = await form.validateFields();
			setAdding(true);
			const role = section === "agency" ? values.role : "Client";
			const normalizedEmail = normalizeEmail(values.email);

			// Submit-time duplicate guard. The Form.Item also surfaces this
			// inline as the user types, but we double-check here in case the
			// participants list mutated between the last keystroke and the
			// Submit click (e.g. another buffered add of the same email).
			const duplicate = findDuplicate(normalizedEmail);
			if (duplicate) {
				setDuplicateHelp(duplicate);
				message.error(`${normalizedEmail}: ${duplicate}`);
				return;
			}

			onAddParticipant(normalizedEmail, role, undefined, emailExists !== false);
			closeModal();
		} catch (error: unknown) {
			if (typeof error === "object" && error !== null && "errorFields" in error) {
				return;
			}
			console.error("Add participant error:", error);
			message.error("Failed to add participant");
		} finally {
			setAdding(false);
		}
	};

	return (
		<>
			<ParticipantTable
				title="Agency"
				columns={[
					{ title: "Name", key: "name" },
					{ title: "Email", key: "email" },
					{ title: "Role", key: "role" },
				]}
				data={agency}
				showRole
				onAdd={() => openModal("agency")}
				onRemove={handleRemove}
				canManage={canManage}
			/>
			<ParticipantTable
				title="Clients"
				columns={[
					{ title: "Name", key: "name" },
					{ title: "Client Email", key: "email" },
					{ title: "Status", key: "status" },
				]}
				data={clients}
				showStatus
				onAdd={() => openModal("clients")}
				onRemove={handleRemoveClient}
				onResend={onResendInvitation}
				// An unsaved invitation has nothing to resend until Save Changes.
				canResend={(p) => invitationIds.has(p.userId) && !p.userId.startsWith("temp-")}
				canManage={canManage}
			/>
			<ParticipantTable
				title="Creators"
				columns={[
					{ title: "Name", key: "name" },
					{ title: "Email", key: "email" },
				]}
				// Read-only: creators are added through Engagement Terms and removed from the creator list.
				data={creators}
				canManage={canManage}
			/>

			<DynamicModal
				title={`Add ${section === "clients" ? "Client" : "Participant"}`}
				open={modalOpen}
				onCancel={closeModal}
				onConfirm={handleOk}
				confirmText="Submit"
				confirmDisabled={!formValid}
				loading={adding}
				width={520}
			>
				<ReservedHelpForm
					form={form}
					layout="vertical"
					onValuesChange={() => setFormValid(isFormValid(form.getFieldsValue()))}
				>
					<Form.Item
						name="email"
						label="Email"
						className="cv-reserve-help"
						validateStatus={
							duplicateHelp
								? "error"
								: isCheckingEmail
									? "validating"
									: emailExists === false
										? section === "clients"
											? ""
											: "error"
										: emailExists === true
											? "success"
											: ""
						}
						help={
							duplicateHelp ? (
								duplicateHelp
							) : isCheckingEmail ? (
								"Checking…"
							) : emailExists === false ? (
								section === "clients" ? (
									// Informational, not a warning: brand purple reads on the modal's lavender.
									<span style={{ color: "#3A0CA3" }}>
										No account yet. We'll email them an invite to sign up.
									</span>
								) : (
									"This email isn’t registered yet."
								)
							) : emailExists === true ? (
								"Email found"
							) : undefined
						}
						rules={[
							{ required: true, message: "Please input email!" },
							{ type: "email", message: "Please enter a valid email!" },
						]}
					>
						<Input placeholder="Enter email" onChange={handleEmailChange} />
					</Form.Item>
					{section === "agency" && (
						<Form.Item
							name="role"
							label="Role"
							rules={[{ required: true, message: "Please select a role!" }]}
						>
							<AppSelect
								placeholder="Select role"
								options={[
									{ label: "Lead", value: "Lead" },
									{ label: "Support", value: "Support" },
									{ label: "Account Manager", value: "Account Manager" },
								]}
							/>
						</Form.Item>
					)}
				</ReservedHelpForm>
			</DynamicModal>
		</>
	);
};
